import type { com } from '@polarsteps/polarsteps-shared-core';

type AutocompleteLocation = com.polarsteps.shared.domain.userdata.AutocompleteLocation;
type GeocodeLocation = com.polarsteps.shared.domain.userdata.GeocodeLocation;

function getGeoJSONBBox(
    bbox: com.polarsteps.shared.domain.location.BoundingBox | null | undefined,
): GeoJSON.BBox | null {
    if (!bbox) {
        return null;
    }
    const minlat = bbox.northEast.latitude;
    const maxlat = bbox.southWest.latitude;
    const minlon = bbox.southWest.longitude;
    const maxlon = bbox.northEast.longitude;
    return [minlon, minlat, maxlon, maxlat];
}

/**
 * Maps AutocompleteLocation from shared codebase to PSLocationWithBB.
 * Returns null when location has no boundingBox (such locations are filtered out).
 */
export function mapAutocompleteLocationToPSLocationWithBB(
    location: AutocompleteLocation,
): PSLocationWithBB | null {
    const boundingbox = getGeoJSONBBox(location.boundingBox);
    if (!boundingbox) {
        return null;
    }

    return {
        id: location.hashCode(),
        name: location.locality,
        lat: location.lat,
        lon: location.lon,
        detail: location.country ?? '',
        fullDetail: location.presentation.fullResult,
        countryCode: location.countryCode ?? '',
        boundingbox,
    };
}

/**
 * Maps GeocodeLocation from shared codebase to PSLocation-like shape for location1toLocation2.
 * Used for reverse geocode results (e.g. searchSpotByLatLon).
 */
export function mapGeocodeLocationToPSLocation(
    location: GeocodeLocation,
): Pick<
    PSLocation,
    'name' | 'detail' | 'fullDetail' | 'countryCode' | 'lat' | 'lon'
> {
    return {
        name: location.locality ?? '',
        detail: location.country ?? '',
        fullDetail:
            location.presentation?.fullResult ??
            location.administrativeArea ??
            '',
        countryCode: location.countryCode ?? '',
        lat: location.lat,
        lon: location.lon,
    };
}
